import React, { useMemo, useRef } from 'react';
import { withStyles, Typography } from '@material-ui/core';
import { connect } from 'react-redux';
import { hasPermission, hasAtLeastOnePermission } from '../../sc-utilities';
import WatchProductAuditRecordList from "./WatchProductAuditRecordList";

const styles = theme => ({
    root: {
        width: `100%`,
        height: 'auto',
        marginTop: '-1%',
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='statusActive']": {
            color: theme.palette.success.dark
        },
        "[class*='statusInactive']": {
            color: theme.palette.error.dark
        },
        "[class*='button']": {
            textTransform: 'none',
            textDecoration: 'none'
        }
    },
    button: {
        textTransform: 'none',
        textDecoration: 'none'
    },
    statusActive: {
        color: theme.palette.success.dark
    },
    statusInactive: {
        color: theme.palette.error.dark
    }
});

const WatchProductAuditRecord = props => {
    const tableRef = useRef();
    const { classes, translations, permissions, editable, columns, productID } = props;
    const visible = columns && columns['AuditRecord'];

    const statusMap = {
        0: "no",
        1: "yes",
        [-1]: "failed",
        [-2]: "pending",
        [-3]: "no_service",
        [-4]: "no_answer",
        [-5]: "no_balance",
    };

    const parseValues = (value) => {
        const key = String(value);
        const tKey = statusMap[key];
        return key === 'null' ? translations["short_not_assigned"] || "n/a" : translations[tKey] ? translations[tKey] : key;
    };

    // Automatically produce: ['0','1','-1','-2','-3','-4','-5','null']
    const allFilters = [
        ...Object.keys(statusMap), // numeric status values as strings
        "null" // explicit null handling
    ];

    // Automatically produce the lookup map based on translations + statusMap
    const lookup = Object.fromEntries(
        allFilters.map(key => {
            if (key === "null") {
                return [key, translations["short_not_assigned"] || "n/a"];
            }

            const tKey = statusMap[key];
            return [key, translations[tKey] || tKey];
        })
    );

    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["carrier"] || 'Carrier', field: 'Carrier',
                hidden: visible ? visible['Carrier'] : false,
                filtering: true,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.Carrier)}</Typography>
            },
            {
                title: translations["sim_lock"] || 'SIM Lock', field: 'SimLock',
                hidden: visible ? visible['SimLock'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.SimLock)}</Typography>
            },
            {
                title: translations["find_my_iphone"] || 'Find my iPhone/FRP', field: 'FindMyIphone',
                hidden: visible ? visible['FindMyIphone'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.FindMyIphone)}</Typography>
            },
            {
                title: translations['carrier_lock'] || 'Carrier Lock', field: 'CarrierLock',
                hidden: visible ? visible['CarrierLock'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.CarrierLock)}</Typography>
            },
            {
                title: 'MDM', field: 'MDM',
                hidden: visible ? visible['MDM'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.MDM)}</Typography>
            },
            {
                title: translations["stolen"] || 'Stolen', field: 'Stolen',
                hidden: visible ? visible['Stolen'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.Stolen)}</Typography>
            },
            {
                title: translations["financed"] || 'Financed', field: 'Financed',
                hidden: visible ? visible['Financed'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.Financed)}</Typography>
            },
            {
                title: translations["warranty_status"] || 'Warranty Status', field: 'WarrantyStatus',
                hidden: visible ? visible['WarrantyStatus'] : false,
                filtering: true,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.WarrantyStatus)}</Typography>
            },
            {
                title: translations["repairs_and_service_coverage"] || 'Repairs And Service Coverage', field: 'RepairsAndServiceCoverage',
                hidden: visible ? visible['RepairsAndServiceCoverage'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.RepairsAndServiceCoverage)}</Typography>
            },
            {
                title: translations["repairs_and_service_expiration_date"] || 'Repairs And Service Expiration Date', field: 'RepairsAndServiceExpirationDate',
                hidden: visible ? visible['RepairsAndServiceExpirationDate'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.RepairsAndServiceExpirationDate)}</Typography>
            },
            {
                title: translations["applecare_eligible"] || 'AppleCare Eligible', field: 'AppleCareEligible',
                hidden: visible ? visible['AppleCareEligible'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.AppleCareEligible)}</Typography>
            },
            {
                title: translations["estimated_purchase_date"] || 'Estimated Purchase Date', field: 'EstimatedPurchaseDate',
                hidden: visible ? visible['EstimatedPurchaseDate'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.EstimatedPurchaseDate)}</Typography>
            },
            {
                title: translations["replaced_device"] || 'Replaced Device', field: 'ReplacedDevice',
                hidden: visible ? visible['ReplacedDevice'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.ReplacedDevice)}</Typography>
            },
            {
                title: translations["loaner_device"] || 'Loaner Device', field: 'LoanerDevice',
                hidden: visible ? visible['LoanerDevice'] : false,
                filtering: true,
                lookup: lookup,
                defaultFilter: allFilters,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.LoanerDevice)}</Typography>
            },
            {
                title: translations["a_number"] || 'ANumber', field: 'ANumber',
                hidden: visible ? visible['ANumber'] : false,
                filtering: true,
                render: rowData => <Typography variant={'caption'}>{parseValues(rowData.ANumber)}</Typography>
            },
            {
                title: translations["external_name"] || 'External name', field: 'ExternalName',
                hidden: visible ? visible['ExternalName'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{rowData.DeviceDataApiID ? rowData.DeviceDataApi?.Name : ""}</Typography>
            },
            {
                title: translations["external_type"] || 'External type', field: 'ExternalType',
                hidden: visible ? visible['ExternalType'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{rowData.DeviceDataApiID ? rowData.DeviceDataApi?.Type : ""}</Typography>
            },
            {
                title: translations["type"] || 'Type', field: 'Type',
                hidden: visible ? visible['Type'] : false,
                filtering: false,
                render: rowData => <Typography variant={'caption'}>{rowData.DeviceDataApiID ? translations["external"] || "External" : translations["internal"] || "Internal"}</Typography>
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible]);

    if (!hasAtLeastOnePermission(permissions, ["product_audit_record_delete"])) {
        tableColumns.shift();
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'product_audit_record_list') &&
                <WatchProductAuditRecordList editable={editable} productID={productID} tableColumns={tableColumns} tableRef={tableRef} />}
        </div >
    );

}
const mapStateToProps = state => {
    return {
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};


export default connect(
    mapStateToProps,
    null,
)(withStyles(styles, { withTheme: true })(WatchProductAuditRecord))
